import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  Row,
} from "antd";
import { useAppDispatch } from "hooks/reduxHooks";
import { notificationController } from "controllers/notificationController";
import { doLogin, doLogout } from "store/slices/authSlice";
import "./LoginForm.css";
import { getModules, getProfileID, getUserType } from "services/localStorage.service";
import { scopeSidebarNavigation } from "components/layouts/sidebarNavigation";
import { getClientV2SidebarNavigation } from "utils/utils";
import jwtDecode from "jwt-decode";


interface LoginFormData {
  username: string;
  password: string;
}

export const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [isLoading, setLoading] = useState(false);

  const handleSubmit = async (values: LoginFormData) => {
    setLoading(true);
    //first logout to remove previous user data from localStorage and sessionStorage
    dispatch(doLogout()).then(async () => {
      try {
        const data: any = await dispatch(doLogin(values)).unwrap();
        const module = getModules();
        const userType = getUserType();
        const userModule = module ? module?.split(",")?.map((module: string) => module.trim().split(/(?=[A-Z])/)[0].toLowerCase()) : null;    
        const basePath = userModule?.includes("dashboard")
          ? "/dashboard"
          : `/${userModule?.[0]}`;
        const pathReplacements: Record<string, string> = {
          "/reports": "/report-v2",
          "/clients": "/client",
          "/pending": "/warehouse-v2",
          "/routes": "/route",
        }
        const updatedBasePath = pathReplacements[basePath] ?? basePath;
        const userModulePath = scopeSidebarNavigation?.find(nav => nav?.url?.startsWith(updatedBasePath))?.url ?? updatedBasePath;
        const isAccountUserScope = userType === "accountV2";
        if (data?.is_account) {
          switch (getProfileID()) {
            case 5:
              navigate("/orders");
              break;
            case 6:
              navigate("/report/list");
              break;
            case 7:
              navigate("/shipment-document-v3/list");
              break;
            case 8:
              navigate("/incoming-soil-v2/list");
              break;
            default:
              // navigate("/dashboard");
              isAccountUserScope ? navigate(userModulePath  || "/dashboard") : navigate("/client/list");
          }
        } else {
          if (getUserType() !== "clientV2") {
            navigate("/dashboard");
            return;
          }

          const token = data.token;
          const decoded = jwtDecode(token);
          // @ts-ignore
          const modules = decoded?.Modules;

          const clientV2NavUrl = getClientV2SidebarNavigation(modules)[0]?.url;

          if (clientV2NavUrl) {
            navigate(clientV2NavUrl);
          }
        }

      } catch (err: any) {
        setLoading(false);
        notificationController.error({ message: err.message });
      } finally {
        setLoading(false);
      }
    }).catch((err: any) => {
      setLoading(false);
      notificationController.error({ message: err.message });
    })
  };


  return (
    <>
      <Row className="login-wrapper">
        <Col span={12}>
          <img
            src="/login-logo.png"
            alt="logo"
            className="logo-img"
          />
        </Col>
        <Col span={12} className="login-form-wraper">
          <div className="login-form">
            <h1 className="welcome-text">Welcome back</h1>
            <p className="details">Please enter your contact details to connect.</p>
            <Form
              layout="vertical"
              className="form-data"
              style={{ padding: 0 }}
              initialValues={{ remember: true }}
              onFinish={handleSubmit}
              requiredMark={false}
              autoComplete="off"
            >
              <Form.Item
                label="Email address"
                name="username"
                rules={[
                  { required: true, message: "Please input your username!" },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="Password"
                name="password"
                rules={[
                  { required: true, message: "Please input your password!" },
                ]}
              >
                <Input.Password />
              </Form.Item>
              <div className="justify-between">
                <Form.Item name="remember" valuePropName="checked" >
                  <Checkbox> <span className="remember">Remember me</span></Checkbox>
                </Form.Item>
                <p className="forgot-text">Forgot password</p>
              </div>

              <Form.Item >
                <Button className="login-btn" htmlType="submit" loading={isLoading}>
                  Log in
                </Button>
              </Form.Item>
            </Form>
          </div>
        </Col>
      </Row>
    </>
  );
};

